/** @jsx jsx */
import { Icon } from "@cd2/components"
import { css, jsx } from "@emotion/core"
import { IComponentProp } from "@cd2/tools"
import React from "react"
import { Link } from "react-router-dom"
import { icons } from "../../constants/icons"
import { InteractivePopout } from "../DropdownMenu"
import { QuickToolTip } from "../QuickTooltip"
import { nd } from "./tokens"

export interface INdAction {
  label: React.ReactNode
  /** In-app route */
  to?: string
  /** External / download link */
  href?: string
  onClick?: () => void
  icon?: IComponentProp<typeof Icon, "icon">
  disabled?: boolean
  /** Shown on hover, usually to explain why the action is disabled */
  tooltip?: React.ReactNode
  destructive?: boolean
}

/**
 * primary: the page's next step. secondary: everything else. tertiary: small grey button for an
 * action attached to one thing on the page (e.g. a line's "Check stock in other branches").
 * plain: a link-style button.
 */
type IButtonVariant = "primary" | "secondary" | "tertiary" | "plain"

function buttonCss(variant: IButtonVariant, disabled: boolean, destructive?: boolean) {
  const base = css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 28px;
    padding: 8px 12px;
    border: none;
    border-radius: ${nd.radius.medium}px;
    font-family: inherit;
    font-size: ${nd.fontSize.body}px;
    font-weight: ${nd.fontWeight.medium};
    line-height: 20px;
    text-decoration: none;
    white-space: nowrap;
    cursor: ${disabled ? "default" : "pointer"};
    transition: background 0.1s ease;

    svg {
      flex: none;
      /* the global "svg { max-width: 100% }" collapses an svg in a shrink-to-fit span to 0 wide */
      max-width: none;
      width: 1em;
      height: 1em;
    }

    &:focus-visible {
      outline: 2px solid ${nd.color.focus};
      outline-offset: 1px;
    }
  `

  if (variant === "plain") {
    return css`
      ${base};
      min-height: 0;
      padding: 0;
      background: none;
      color: ${disabled ? nd.color.textDisabled : nd.color.link};
      &:hover {
        text-decoration: ${disabled ? "none" : "underline"};
      }
    `
  }

  if (variant === "tertiary") {
    return css`
      ${base};
      min-height: 24px;
      padding: 2px 8px;
      gap: 4px;
      border-radius: ${nd.radius.small}px;
      font-size: 13px;
      line-height: 18px;
      background: ${disabled ? nd.color.surfaceSubdued : nd.color.surfaceSelected};
      color: ${disabled ? nd.color.textDisabled : nd.color.text};
      &:hover {
        background: ${disabled ? nd.color.surfaceSubdued : nd.color.borderStrong};
        color: ${disabled ? nd.color.textDisabled : nd.color.text};
      }
    `
  }

  if (disabled) {
    return css`
      ${base};
      background: ${nd.color.surfaceSubdued};
      color: ${nd.color.textDisabled};
      box-shadow: inset 0 0 0 1px ${nd.color.border};
    `
  }

  if (variant === "primary" && destructive) {
    return css`
      ${base};
      background: ${nd.color.critical};
      color: white;
      box-shadow: ${nd.shadow.buttonPrimary};
      &:hover {
        background: ${nd.tone.critical.text};
        color: white;
      }
    `
  }

  if (variant === "primary") {
    return css`
      ${base};
      background: ${nd.color.primary};
      color: white;
      box-shadow: ${nd.shadow.buttonPrimary};
      svg {
        color: ${nd.color.primaryIcon};
      }
      &:hover {
        background: ${nd.color.primaryHover};
        color: white;
      }
    `
  }

  return css`
    ${base};
    background: ${nd.color.surface};
    color: ${destructive ? nd.color.critical : nd.color.text};
    box-shadow: ${nd.shadow.button};
    &:hover {
      background: ${nd.color.surfaceHover};
      color: ${destructive ? nd.color.critical : nd.color.text};
    }
  `
}

/** A Shopify style button. Renders a router Link for `to`, an anchor for `href`, else a button. */
export function Button({
  variant = "secondary",
  loading,
  disclosure,
  className,
  ...action
}: INdAction & {
  variant?: IButtonVariant
  loading?: boolean
  /** Adds a trailing chevron, for buttons that open a menu */
  disclosure?: boolean
  className?: string
}) {
  const disabled = !!action.disabled || !!loading
  const content = (
    <React.Fragment>
      {loading ? <Icon icon={icons.spinner} spin /> : action.icon && <Icon icon={action.icon} />}
      {action.label}
      {disclosure && <Icon icon={icons.chevronDown} size={10} />}
    </React.Fragment>
  )

  const styles = buttonCss(variant, disabled, action.destructive)
  const node =
    action.to && !disabled ? (
      <Link to={action.to} css={styles} className={className}>
        {content}
      </Link>
    ) : action.href && !disabled ? (
      <a href={action.href} target="_blank" rel="noreferrer" css={styles} className={className}>
        {content}
      </a>
    ) : (
      <button
        type="button"
        css={styles}
        className={className}
        disabled={disabled}
        onClick={disabled ? undefined : action.onClick}
      >
        {content}
      </button>
    )

  if (action.tooltip) {
    return (
      <QuickToolTip content={action.tooltip}>
        <span
          css={css`
            display: inline-flex;
          `}
        >
          {node}
        </span>
      </QuickToolTip>
    )
  }
  return node
}

/** A row of buttons with Shopify's standard spacing. */
export function ButtonGroup({ children }: { children: React.ReactNode }) {
  return (
    <div
      css={css`
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: ${nd.space.sm}px;
      `}
    >
      {children}
    </div>
  )
}

/** A "More actions" style button that opens a list of actions. Falsy actions are skipped. */
export function ActionMenu({
  label = "More actions",
  actions,
  variant,
}: {
  label?: React.ReactNode
  /** Match the variant of the buttons beside it, so a row of them has the same corners */
  variant?: IButtonVariant
  actions: (INdAction | false | null | undefined)[]
}) {
  const visible = actions.filter((a): a is INdAction => !!a)
  if (visible.length === 0) {
    return null
  }
  return (
    <InteractivePopout
      closeOnInsideClick
      content={
        <div
          css={css`
            display: grid;
            min-width: 200px;
            max-width: calc(100vw - 24px);
            padding: 6px;
            background: ${nd.color.surface};
            border-radius: ${nd.radius.large}px;
            box-shadow: ${nd.shadow.popover};
            font-family: ${nd.font};
          `}
        >
          {visible.map((action, i) => (
            <ActionMenuItem key={i} {...action} />
          ))}
        </div>
      }
    >
      <Button label={label} variant={variant} disclosure />
    </InteractivePopout>
  )
}

function ActionMenuItem(action: INdAction) {
  const disabled = !!action.disabled
  const styles = css`
    display: flex;
    align-items: center;
    gap: ${nd.space.sm}px;
    width: 100%;
    padding: 6px ${nd.space.sm}px;
    border: none;
    border-radius: ${nd.radius.medium}px;
    background: none;
    font-family: inherit;
    font-size: ${nd.fontSize.body}px;
    font-weight: ${nd.fontWeight.regular};
    line-height: 20px;
    text-align: left;
    text-decoration: none;
    color: ${disabled
      ? nd.color.textDisabled
      : action.destructive
      ? nd.color.critical
      : nd.color.text};
    cursor: ${disabled ? "default" : "pointer"};
    &:hover {
      background: ${disabled ? "none" : nd.color.surfaceHover};
      color: ${disabled
        ? nd.color.textDisabled
        : action.destructive
        ? nd.color.critical
        : nd.color.text};
    }
  `
  const content = (
    <React.Fragment>
      {action.icon && (
        <span
          css={css`
            width: 16px;
            display: inline-flex;
            justify-content: center;
            color: ${disabled ? nd.color.textDisabled : nd.color.textSubdued};
          `}
        >
          <Icon icon={action.icon} />
        </span>
      )}
      {action.label}
    </React.Fragment>
  )

  const node =
    action.to && !disabled ? (
      <Link to={action.to} css={styles}>
        {content}
      </Link>
    ) : action.href && !disabled ? (
      <a href={action.href} target="_blank" rel="noreferrer" css={styles}>
        {content}
      </a>
    ) : (
      <button
        type="button"
        css={styles}
        disabled={disabled}
        onClick={e => {
          if (disabled) {
            // keep the menu open so the tooltip explaining why can be read
            e.stopPropagation()
            return
          }
          action.onClick?.()
        }}
      >
        {content}
      </button>
    )

  if (action.tooltip) {
    return (
      <QuickToolTip content={action.tooltip} placement="top">
        <div>{node}</div>
      </QuickToolTip>
    )
  }
  return node
}

/**
 * A square, icon only button for tight spots such as a table row's remove button. `label` is
 * read out by screen readers and shown on hover.
 */
export function IconButton({
  icon,
  label,
  onClick,
  disabled,
  destructive,
}: {
  icon: IComponentProp<typeof Icon, "icon">
  label: string
  onClick: () => void
  disabled?: boolean
  destructive?: boolean
}) {
  return (
    <QuickToolTip content={label}>
      <button
        type="button"
        aria-label={label}
        disabled={disabled}
        onClick={disabled ? undefined : onClick}
        css={css`
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: none;
          width: 32px;
          height: 32px;
          border: none;
          border-radius: ${nd.radius.medium}px;
          background: none;
          color: ${disabled ? nd.color.textDisabled : nd.color.textSubdued};
          cursor: ${disabled ? "default" : "pointer"};
          &:hover {
            background: ${disabled ? "none" : nd.color.surfaceHover};
            color: ${disabled
              ? nd.color.textDisabled
              : destructive
              ? nd.color.critical
              : nd.color.text};
          }
          &:focus-visible {
            outline: 2px solid ${nd.color.focus};
            outline-offset: 1px;
          }
        `}
      >
        <Icon icon={icon} />
      </button>
    </QuickToolTip>
  )
}
